import { isBackendConfigured, supabase } from "./supabase";
import { levelForXp, reviewTrustDelta, XP_ACTIONS } from "./gameEngine";
import type {
  ActiveClaim, ClaimChecklist, CircleMember, LeaderboardEntry, LedgerEntry, MemberNotification,
  MemberProfile, MiddlemanRoute, MissionReview, Opportunity, ProvingSubmission, ReviewDecision, XpLogEntry,
} from "./types";

export const isFixtureMode = import.meta.env.DEV && (
  import.meta.env.VITE_USE_FIXTURES === "true" || !isBackendConfigured
);

async function fixtures() {
  if (!import.meta.env.DEV) throw new Error("Development fixtures are unavailable in production.");
  return import("./fixtures");
}

export async function getMemberProfile(): Promise<MemberProfile> {
  if (isFixtureMode) return (await fixtures()).readFixtureState().profile;
  if (!supabase) throw new Error("Backend is not configured.");
  const { data, error } = await supabase.from("member_dashboard_profiles").select("*").single();
  if (error) throw error;
  return {
    id: data.id, handle: data.handle, role: data.role, rank: data.rank,
    activeRoute: data.route ?? "unplaced", trustScore: data.trust_score ?? 100, sponsorId: data.sponsor_id,
    xp: data.xp ?? 0, level: data.level ?? 0,
    verifiedResults: data.verified_results, minorAccessApproved: data.minor_access_approved,
    membershipStatus: data.membership_status, legalApproved: data.legal_approved, payoutEligible: data.payout_eligible,
  };
}

export async function listOpportunities(): Promise<Opportunity[]> {
  if (isFixtureMode) return (await fixtures()).readFixtureState().opportunities.filter((item) => item.status === "open");
  if (!supabase) throw new Error("Backend is not configured.");
  const { data, error } = await supabase.from("opportunities")
    .select("id,twenty_id,brand,market,warm_signal,ai_score,expected_invoice,currency,public_summary,status,route_required,outcome,deliverable,bounty_min,bounty_max,high_value")
    .eq("status", "open").order("ai_score", { ascending: false });
  if (error) throw error;
  return (data ?? []).map((row) => ({
    id: row.id, twentyId: row.twenty_id, brand: row.brand, market: row.market,
    signal: row.warm_signal, aiScore: row.ai_score, expectedInvoice: Number(row.expected_invoice),
    currency: row.currency, publicSummary: row.public_summary, status: row.status as Opportunity["status"],
    routeRequired: row.route_required ?? "closer",
    outcome: row.outcome, deliverable: row.deliverable,
    bountyMin: row.bounty_min == null ? undefined : Number(row.bounty_min),
    bountyMax: row.bounty_max == null ? undefined : Number(row.bounty_max),
    highValue: row.high_value ?? false,
  }));
}

export async function getActiveClaim(): Promise<ActiveClaim | null> {
  if (isFixtureMode) return (await fixtures()).readFixtureState().claim;
  if (!supabase) throw new Error("Backend is not configured.");
  const { data, error } = await supabase.from("claims")
    .select("id,opportunity_id,expires_at,opportunity:opportunities(id,brand)")
    .eq("status", "active").maybeSingle();
  if (error) throw error;
  if (!data) return null;
  const related = Array.isArray(data.opportunity) ? data.opportunity[0] : data.opportunity;
  return { id: data.id, opportunityId: data.opportunity_id, expiresAt: data.expires_at, opportunity: related ? { brand: related.brand } as Opportunity : undefined };
}

export async function claimOpportunity(opportunityId: string) {
  if (isFixtureMode) {
    const api = await fixtures();
    const state = api.readFixtureState();
    if (state.claim) throw new Error("You already have an active claim.");
    const opportunity = state.opportunities.find((item) => item.id === opportunityId);
    if (!opportunity || opportunity.status !== "open") throw new Error("Opportunity is no longer available.");
    opportunity.status = "claimed";
    state.claim = {
      id: crypto.randomUUID(), opportunityId, expiresAt: new Date(Date.now() + 86_400_000).toISOString(), opportunity,
    };
    api.writeFixtureState(state);
    return;
  }
  const { error } = await supabase!.rpc("claim_opportunity", { p_opportunity_id: opportunityId });
  if (error) throw error;
}

export async function releaseClaim(claimId: string) {
  if (isFixtureMode) {
    const api = await fixtures();
    const state = api.readFixtureState();
    if (!state.claim || state.claim.id !== claimId) throw new Error("Active claim not found.");
    const opportunity = state.opportunities.find((item) => item.id === state.claim?.opportunityId);
    if (opportunity) opportunity.status = "open";
    state.claim = null;
    state.checklist = { confirmedIdentity: false, reviewedContext: false, preparedQuestions: false, confirmedTiming: false, acceptedTerms: false };
    api.writeFixtureState(state);
    return;
  }
  const { error } = await supabase!.rpc("release_claim", { p_claim_id: claimId });
  if (error) throw error;
}

export async function getChecklist(): Promise<ClaimChecklist> {
  if (isFixtureMode) return (await fixtures()).readFixtureState().checklist;
  const { data, error } = await supabase!.from("claim_checklists").select("*").single();
  if (error) throw error;
  return {
    confirmedIdentity: data.confirmed_identity, reviewedContext: data.reviewed_context,
    preparedQuestions: data.prepared_questions, confirmedTiming: data.confirmed_timing,
    acceptedTerms: data.accepted_terms,
  };
}

export async function saveChecklist(checklist: ClaimChecklist) {
  if (isFixtureMode) {
    const api = await fixtures();
    const state = api.readFixtureState();
    state.checklist = checklist;
    api.writeFixtureState(state);
    return;
  }
  const claim = await getActiveClaim();
  if (!claim) throw new Error("Active claim required.");
  const { error } = await supabase!.from("claim_checklists").update({
    confirmed_identity: checklist.confirmedIdentity,
    reviewed_context: checklist.reviewedContext,
    prepared_questions: checklist.preparedQuestions,
    confirmed_timing: checklist.confirmedTiming,
    accepted_terms: checklist.acceptedTerms,
    updated_at: new Date().toISOString(),
  }).eq("claim_id", claim.id);
  if (error) throw error;
}

export async function getClaimContact(): Promise<{ name: string; email: string }> {
  if (isFixtureMode) return { name: "Maya Kline", email: "maya@example-client.test" };
  const claim = await getActiveClaim();
  if (!claim) throw new Error("Active claim required.");
  const { data, error } = await supabase!.rpc("get_claim_contact", { p_claim_id: claim.id });
  if (error) throw error;
  return data as { name: string; email: string };
}

export async function getWorkSession(): Promise<string | null> {
  if (isFixtureMode) return (await fixtures()).readFixtureState().workSessionStart;
  const { data, error } = await supabase!.from("work_sessions").select("started_at").is("ended_at", null).maybeSingle();
  if (error) throw error;
  return data?.started_at ?? null;
}

export async function startWorkSession() {
  const claim = await getActiveClaim();
  if (!claim) throw new Error("Active claim required.");
  if (isFixtureMode) {
    const api = await fixtures();
    const state = api.readFixtureState();
    state.workSessionStart = new Date().toISOString();
    api.writeFixtureState(state);
    return;
  }
  const { error } = await supabase!.rpc("start_work_session", { p_claim_id: claim.id });
  if (error) throw error;
}

export async function stopWorkSession() {
  if (isFixtureMode) {
    const api = await fixtures();
    const state = api.readFixtureState();
    state.workSessionStart = null;
    api.writeFixtureState(state);
    return;
  }
  const { error } = await supabase!.rpc("stop_work_session");
  if (error) throw error;
}

export async function submitEvidence({ note, file }: { note: string; file?: File | null }) {
  const claim = await getActiveClaim();
  if (!claim) throw new Error("Active claim required.");
  if (isFixtureMode) {
    const api = await fixtures();
    const state = api.readFixtureState();
    state.evidenceNote = note;
    state.workSessionStart = null;
    state.notifications.unshift({ id: crypto.randomUUID(), title: "Task submitted", message: "Your proof is waiting for a Level 6+ peer review.", createdAt: new Date().toISOString(), read: false });
    state.claim = null;
    api.writeFixtureState(state);
    return;
  }
  let storagePath = "";
  if (file) {
    const { data: user } = await supabase!.auth.getUser();
    storagePath = `${user.user?.id}/${claim.id}/${crypto.randomUUID()}-${file.name.replaceAll(/[^a-zA-Z0-9._-]/g, "_")}`;
    const { error: uploadError } = await supabase!.storage.from("work-evidence").upload(storagePath, file, { upsert: false });
    if (uploadError) throw uploadError;
  }
  const { error } = await supabase!.rpc("submit_claim_evidence", {
    p_claim_id: claim.id, p_evidence_type: file ? "audio" : "call_note", p_storage_path: storagePath, p_note: note,
  });
  if (error) throw error;

  supabase!.functions.invoke("twenty-push-evidence", {
    body: { type: "INSERT", record: { claim_id: claim.id, note: note } }
  }).catch(console.error);
}

export async function listLedger(): Promise<LedgerEntry[]> {
  if (isFixtureMode) return (await fixtures()).readFixtureState().ledger;
  const { data, error } = await supabase!.from("ledger_entries").select("*").order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []).map((row) => ({
    id: row.id, source: `${row.invoice_reference} / ${row.source_role}`, amount: Number(row.amount),
    currency: row.currency, rate: Number(row.rate), status: row.status, createdAt: row.created_at,
    externalReference: row.external_payment_reference,
  }));
}

export async function listCircle(): Promise<CircleMember[]> {
  if (isFixtureMode) return (await fixtures()).readFixtureState().circle;
  return [];
}

export async function listNotifications(): Promise<MemberNotification[]> {
  if (isFixtureMode) return (await fixtures()).readFixtureState().notifications;
  const { data, error } = await supabase!.from("notifications").select("id,template,status,created_at").order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []).map((row) => ({ id: row.id, title: row.template, message: row.status, createdAt: row.created_at, read: false }));
}

export async function updateRoute(route: Exclude<MiddlemanRoute, "unplaced">) {
  if (isFixtureMode) {
    const api = await fixtures();
    const state = api.readFixtureState();
    const firstChoice = state.profile.activeRoute === "unplaced";
    state.profile.activeRoute = route;
    if (firstChoice) {
      state.profile.xp += XP_ACTIONS.choose_route;
      const level = levelForXp(state.profile.xp);
      state.profile.level = level.level;
      state.profile.rank = `L${level.level} ${level.title.toUpperCase()}`;
      state.xpLog.unshift({ id: crypto.randomUUID(), action: "Route selected", amount: XP_ACTIONS.choose_route, createdAt: new Date().toISOString() });
    }
    api.writeFixtureState(state);
    return;
  }
  const { error } = await supabase!.rpc("choose_distribution_route", { p_route: route });
  if (error) throw error;
}

export async function listPendingReviews(): Promise<MissionReview[]> {
  if (isFixtureMode) return (await fixtures()).readFixtureState().reviews.filter(r => r.status === "pending");
  if (!supabase) throw new Error("Backend is not configured.");
  const { data, error } = await supabase.from("submission_reviews")
    .select("id, submission_id, status, created_at, notes, rubric_snapshot, mission_submissions(assignment_id, summary, submission_evidence(requirement_key, kind, text_value, url_value, storage_path), mission_assignments(workflows(opportunities(brand))))")
    .in("status", ["pending", "unassigned"])
    .order("created_at", { ascending: false });
  if (error) throw error;
  type ReviewRow = {
    id: string;
    submission_id: string;
    status: MissionReview["status"] | "unassigned" | "changes_requested";
    created_at: string;
    notes: string;
    rubric_snapshot: Array<{ label?: string; title?: string; key?: string }> | string[];
    mission_submissions?: {
      assignment_id?: string;
      summary?: string;
      submission_evidence?: Array<{
        requirement_key?: string;
        kind?: string;
        text_value?: string;
        url_value?: string;
        storage_path?: string;
      }>;
      mission_assignments?: {
        workflows?: { opportunities?: { brand?: string } };
      };
    };
  };
  return ((data ?? []) as unknown as ReviewRow[]).map((row) => ({
    id: row.id,
    claimId: row.mission_submissions?.assignment_id ?? row.submission_id,
    brand: row.mission_submissions?.mission_assignments?.workflows?.opportunities?.brand ?? "Submitted task",
    status: row.status === "approved" || row.status === "rejected" ? row.status : "pending",
    createdAt: row.created_at,
    sopChecklist: normalizeWorkflowReviewChecklist(row.rubric_snapshot),
    honeypot: false,
    expectedDecision: undefined,
    evidenceNote: summarizeWorkflowEvidence(row.mission_submissions?.summary, row.mission_submissions?.submission_evidence),
  }));
}

export async function submitReviewResult(reviewId: string, status: ReviewDecision, notes: string) {
  if (isFixtureMode) {
    const api = await fixtures();
    const state = api.readFixtureState();
    const review = state.reviews.find(r => r.id === reviewId);
    if (!review) throw new Error("Review not found.");
    review.status = status;
    const delta = reviewTrustDelta({ decision: status, honeypot: review.honeypot, expectedDecision: review.expectedDecision });
    state.profile.trustScore = Math.max(0, Math.min(100, state.profile.trustScore + delta));
    const xp = review.honeypot && status === "rejected" ? XP_ACTIONS.catch_bad_work : XP_ACTIONS.accurate_review;
    if (!review.honeypot || status === review.expectedDecision) {
      state.profile.xp += xp;
      state.xpLog.unshift({ id: crypto.randomUUID(), action: review.honeypot ? "Caught bad work" : "Accurate peer review", amount: xp, createdAt: new Date().toISOString() });
    }
    const level = levelForXp(state.profile.xp);
    state.profile.level = level.level;
    state.profile.rank = `L${level.level} ${level.title.toUpperCase()}`;
    state.notifications.unshift({
      id: crypto.randomUUID(),
      title: review.honeypot ? "Trust check resolved" : "Review recorded",
      message: delta < 0 ? `Trust fell ${Math.abs(delta)} points.` : `Trust increased ${delta} points. +${xp} XP.`,
      createdAt: new Date().toISOString(),
      read: false,
    });
    api.writeFixtureState(state);
    return;
  }
  const { error } = await supabase!.rpc("resolve_submission_review", {
    p_review_id: reviewId, p_decision: status, p_notes: notes,
  });
  if (error) throw error;
}

function normalizeWorkflowReviewChecklist(snapshot: Array<{ label?: string; title?: string; key?: string }> | string[] | undefined) {
  if (!Array.isArray(snapshot)) return [];
  return snapshot.map((item) => {
    if (typeof item === "string") return item;
    return item.label ?? item.title ?? item.key ?? "";
  }).filter(Boolean);
}

function summarizeWorkflowEvidence(summary?: string, evidence?: Array<{ requirement_key?: string; kind?: string; text_value?: string; url_value?: string; storage_path?: string }>) {
  const lines = (evidence ?? []).map((item) => {
    const value = item.url_value ?? item.text_value ?? item.storage_path ?? "";
    return `${item.requirement_key ?? item.kind ?? "proof"}: ${value}`;
  });
  return [summary, ...lines].filter(Boolean).join("\n");
}

export async function listProvingMissions(): Promise<ProvingSubmission[]> {
  if (isFixtureMode) return (await fixtures()).readFixtureState().proving;
  const { data, error } = await supabase!.from("proving_submissions")
    .select("step,title,status,instructions,submission").order("step");
  if (error) throw error;
  return (data ?? []) as ProvingSubmission[];
}

export async function submitProvingMission(step: number, submission: string) {
  if (isFixtureMode) {
    const api = await fixtures();
    const state = api.readFixtureState();
    const mission = state.proving.find((item) => item.step === step);
    if (!mission || mission.status === "locked") throw new Error("This proving task is locked.");
    mission.status = "submitted";
    mission.submission = submission;
    api.writeFixtureState(state);
    return;
  }
  const { error } = await supabase!.rpc("submit_proving_mission", { p_step: step, p_submission: submission });
  if (error) throw error;
}

export async function listLeaderboard(): Promise<LeaderboardEntry[]> {
  if (isFixtureMode) return (await fixtures()).readFixtureState().leaderboard;
  const { data, error } = await supabase!.from("weekly_leaderboard").select("*").order("position");
  if (error) throw error;
  return (data ?? []).map((row) => ({
    id: row.id, handle: row.handle, route: row.route, weeklyXp: row.weekly_xp,
    approvedMissions: row.approved_missions, trustScore: row.trust_score, position: row.position,
  }));
}

export async function listXpLog(): Promise<XpLogEntry[]> {
  if (isFixtureMode) return (await fixtures()).readFixtureState().xpLog;
  const { data, error } = await supabase!.from("xp_log").select("id,action,amount,created_at").order("created_at", { ascending: false }).limit(20);
  if (error) throw error;
  return (data ?? []).map((row) => ({ id: row.id, action: row.action, amount: row.amount, createdAt: row.created_at }));
}
